import Swal from "sweetalert2";
import type { SweetAlertIcon, SweetAlertOptions, SweetAlertResult } from "sweetalert2";
import { brand, colors, scaleOf } from "../../config/theme";

const DIALOG_CONFIRM_COLORS = {
  primary: brand.primary,
  danger: scaleOf(colors.light, "error")["500"],
  success: scaleOf(colors.light, "success")["500"],
  warning: scaleOf(colors.light, "warning")["500"],
} as const satisfies Record<string, string>;

export type DialogTone = keyof typeof DIALOG_CONFIRM_COLORS;

export const DIALOG_COLORS = {
  ...DIALOG_CONFIRM_COLORS,
  cancel: colors.light.text.muted,
} as const satisfies Record<string, string>;

export interface DialogThemeOptions {
  confirmButtonColor: string;
  cancelButtonColor: string;
  reverseButtons: boolean;
}

/**
 * El aspecto del diálogo lo define `.swal2-popup` en index.css, así que cubre
 * también los `Swal.fire` directos. Esto solo aporta los colores del tono.
 */
export function dialogTheme(tone: DialogTone = "primary"): DialogThemeOptions {
  return {
    confirmButtonColor: DIALOG_CONFIRM_COLORS[tone],
    cancelButtonColor: colors.light.text.muted,
    reverseButtons: true,
  };
}

export function showDialog<T extends SweetAlertOptions>(
  options: T,
  tone: DialogTone = "primary",
): Promise<SweetAlertResult> {
  return Swal.fire({ ...dialogTheme(tone), ...options });
}

export interface ConfirmOptions {
  title: string;
  text?: string;
  html?: string;
  confirmText?: string;
  cancelText?: string;
  tone?: DialogTone;
  icon?: SweetAlertIcon;
}

export async function confirmDialog({
  title,
  text,
  html,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  tone = "primary",
  icon = "question",
}: ConfirmOptions): Promise<boolean> {
  const result = await showDialog(
    {
      title,
      text,
      html,
      icon,
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: cancelText,
      focusCancel: tone === "danger",
    },
    tone,
  );
  return result.isConfirmed;
}

export function confirmDelete(options: Omit<ConfirmOptions, "tone" | "icon">): Promise<boolean> {
  return confirmDialog({ icon: "warning", confirmText: "Eliminar", ...options, tone: "danger" });
}

export function notifySuccess(title: string, text?: string): Promise<SweetAlertResult> {
  return showDialog({ title, text, icon: "success" }, "success");
}

export function notifyError(title: string, text?: string): Promise<SweetAlertResult> {
  return showDialog({ title, text, icon: "error" }, "danger");
}

export function notifyInfo(title: string, text?: string): Promise<SweetAlertResult> {
  return showDialog({ title, text, icon: "info" });
}

export function showLoadingDialog(title: string, text?: string): void {
  void showDialog({
    title,
    text,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });
}

export function closeDialog(): void {
  Swal.close();
}
